import React from 'react';
import { observer } from 'mobx-react';
import {
	IButtonStyles,
	IconButton,
	Label,
	Separator,
	Spinner,
	Stack,
	getTheme,
	Text,
	Checkbox,
	Slider,
	Dropdown,
	TextField,
} from '@fluentui/react';
import { FilterOptionsState } from '../../state/FilterOptionsState';
import {
	FieldType,
	IEntityColumn,
	IFieldOption,
	TaskRequestFilterFieldComparator,
} from '../../API';
import { FiltersState } from '../../state/FiltersState';
import { IUserConfigPackage } from '../../API';
import EntityDashboard from '../dashboards/EntityDashboard';

interface IFilterOptionsList {
	state: FilterOptionsState;
	filters: FiltersState;
}

class FilterOptionsList extends React.Component<IFilterOptionsList> {
	render() {
		const theme = getTheme();
		const iconButtonStyles: Partial<IButtonStyles> = {
			root: {
				color: theme.palette.neutralPrimary,
				marginLeft: 'auto',
				marginRight: '2px',
			},
			rootHovered: {
				color: theme.palette.neutralDark,
			},
		};

		return (
			<>
				<Stack horizontal horizontalAlign={'space-between'}>
					<Label>{this.props.state.columnInfo.displayName}</Label>
					<IconButton
						styles={iconButtonStyles}
						iconProps={{ iconName: 'ChevronDownMed' }}
						ariaLabel="Expand Filter Option"
						onClick={() => {
							this.props.state.expandFilterOptions(!this.props.state.show);
						}}
					/>
				</Stack>
				<Separator />

				{this.props.state.show && <Stack>{this._getFilterOptionsContent()}</Stack>}
			</>
		);
	}

	private _getFilterOptionsContent(): React.ReactNode {
		if (this.props.state.loading) {
			return <Spinner></Spinner>;
		}

		if (
			this.props.state.filterOptions &&
			this.props.state.filterOptions.length > 0
		) {
			return <Stack tokens={{ childrenGap: 5 }}>{this.getOptions()}</Stack>;
		}

		return <Text>There are no filters available for this field.</Text>;
	}

	private onFilterOptionChange = (
		fieldOption: IFieldOption,
		index: number,
		checked: boolean,
	): void => {
		if (checked) {
			this.props.state.addFilterOption(fieldOption);
			this.props.filters.addFilter(
				this.props.state.columnInfo.id,
				fieldOption.internal_value,
				TaskRequestFilterFieldComparator.Equals,
			);
		} else {
			this.props.state.removeFilterOption(fieldOption);
			this.props.filters.removeFilter(
				this.props.state.columnInfo.id,
				fieldOption.internal_value,
			);
		}

		this.props.filters.applyFilters();
	};

	private getOptions(): React.ReactNode | React.ReactNode[] {
		if (
			this.props.state.columnInfo.fieldType === FieldType.DateTime &&
			this.props.state.filterOptions.length === 2
		) {
			return this.renderDateLabel(this.props.state.filterOptions);
		}

		let meSelected = false;
		let allOptions: any = [];

		if (this.props.state.columnInfo.fieldType === FieldType.User) {
			meSelected =
				this.props.state.selectedFilterOptions.length === 1 &&
				this.props.state.selectedFilterOptions[0].internal_value === '[me]';
			allOptions.push(
				<Checkbox
					key={this.props.state.columnInfo.id + 'meoption'}
					label="Me"
					onChange={(_, checked) => {
						if (checked) {
							this.props.state.setFilterOptions([
								{ internal_value: '[me]', display_value: 'Me' },
							]);
						} else {
							this.props.state.setFilterOptions([]);
						}
						this.onSelectionChanged();
					}}
					checked={meSelected}
				/>,
			);
		}

		if (!meSelected) {
			allOptions = allOptions.concat(
				this.props.state.filterOptions.map((option, index) => {
					return (
						<Checkbox
							key={index}
							onRenderLabel={() =>
								FilterOptionsList.renderLabel(
									this.props.state.userConfig,
									option,
									this.props.state.columnInfo,
								)
							}
							onChange={(_, checked) =>
								this.onFilterOptionChange(option, index, !!checked)
							}
							checked={this.props.state.isChecked(option.internal_value)}
						/>
					);
				}),
			);
		}

		return allOptions;
	}

	public static renderLabel(
		userConfig: IUserConfigPackage,
		option: IFieldOption,
		customField: IEntityColumn,
	) {
		if (option.internal_value === '') {
			return <Text>No Value</Text>;
		}

		return EntityDashboard.displayPropertyValue(
			null,
			userConfig,
			customField,
			option.internal_value,
			true,
		);
	}

	private getSelectedDateDropdownKey(): string {
		if (this.props.state.selectedFilterOptions.length !== 1) {
			return '';
		} else {
			let days = Number(this.props.state.selectedFilterOptions[0].internal_value);
			return days >= 0 ? 'next' : 'prev';
		}
	}

	private onSelectionChanged = () => {
		this.props.filters.removeFiltersForField(this.props.state.columnInfo.id);

		if (this.props.state.columnInfo.fieldType === FieldType.DateTime) {
			if (this.props.state.selectedFilterOptions.length === 1) {
				this.props.filters.addFilter(
					this.props.state.columnInfo.id,
					this.props.state.selectedFilterOptions[0].internal_value,
					TaskRequestFilterFieldComparator.DaysOffset,
				);
			} else {
				this.props.filters.addFilter(
					this.props.state.columnInfo.id,
					this.props.state.selectedFilterOptions[0].internal_value,
					TaskRequestFilterFieldComparator.GreaterThanEquals,
				);

				this.props.filters.addFilter(
					this.props.state.columnInfo.id,
					this.props.state.selectedFilterOptions[1].internal_value,
					TaskRequestFilterFieldComparator.LessThanEquals,
				);
			}
		} else {
			this.props.state.selectedFilterOptions.forEach((selectedOption) => {
				let comparator = TaskRequestFilterFieldComparator.Equals;
				if (selectedOption.internal_value === '[me]') {
					comparator = TaskRequestFilterFieldComparator.Contains;
				}

				this.props.filters.addFilter(
					this.props.state.columnInfo.id,
					selectedOption.internal_value,
					comparator,
				);
			});
		}

		this.props.filters.applyFilters();
	};

	public renderDateLabel(options: IFieldOption[]): React.ReactNode {
		const sliderValueFormat = (value: number) => {
			return new Intl.DateTimeFormat(navigator.language).format(value);
		};

		const onFixedDatesChange = (_: any, range?: [number, number]) => {
			if (range) {
				this.props.state.setFilterOptionDates(
					new Date(range[0]),
					new Date(range[1]),
				);
			}
		};

		const getSelectedDateFilterOrDefault = (
			index: number,
			defaultDate: Date,
		): Date => {
			return this.props.state.selectedFilterOptions.length > 1
				? new Date(this.props.state.selectedFilterOptions[index].internal_value)
				: defaultDate;
		};

		if (
			options.length === 2 &&
			options[0].internal_value !== '' &&
			options[1].internal_value !== ''
		) {
			let minDate = new Date(options[0].internal_value);
			let maxDate = new Date(options[1].internal_value);

			let selectedDropdownKey = this.getSelectedDateDropdownKey();
			let days =
				this.props.state.selectedFilterOptions.length === 1
					? this.props.state.selectedFilterOptions[0].internal_value
					: '30';

			return (
				<div>
					<Dropdown
						selectedKey={selectedDropdownKey}
						onChange={(_, o) => {
							if (!o?.key) {
								//It should be fixed dates
								if (this.props.state.selectedFilterOptions.length === 1) {
									this.props.state.setFilterOptionDates(minDate, maxDate);
								}
							} else {
								let days = o.key === 'next' ? '30' : '-30';

								this.props.state.setFilterOptions([
									{
										display_value: days,
										internal_value: days,
									},
								]);
							}
							this.onSelectionChanged();
						}}
						options={[
							{ key: '', text: 'Specific dates' },
							{ key: 'next', text: 'In the next' },
							{ key: 'prev', text: 'In the previous' },
						]}
					/>

					{selectedDropdownKey ? (
						<>
							<Stack horizontal style={{ marginTop: 10 }}>
								<TextField
									style={{ width: 50 }}
									value={Math.abs(Number(days)).toString()}
									onChange={(_, newDays) => {
										if (newDays) {
											if (selectedDropdownKey === 'prev') {
												newDays = '-' + newDays;
											}

											this.props.state.setFilterOptions([
												{
													display_value: newDays,
													internal_value: newDays,
												},
											]);

											this.onSelectionChanged();
										}
									}}
								/>
								<Label style={{ marginLeft: 10 }}>days</Label>
							</Stack>
						</>
					) : (
						<>
							<Slider
								min={minDate.getTime()}
								max={maxDate.getTime()}
								defaultLowerValue={minDate.getTime()}
								defaultValue={maxDate.getTime()}
								lowerValue={getSelectedDateFilterOrDefault(0, minDate).getTime()}
								value={getSelectedDateFilterOrDefault(1, maxDate).getTime()}
								step={86400000}
								valueFormat={sliderValueFormat}
								ranged
								showValue={false}
								onChanged={this.onSelectionChanged}
								onChange={onFixedDatesChange}
							/>
							<Stack horizontal horizontalAlign={'space-between'}>
								<div>
									{new Intl.DateTimeFormat(navigator.language).format(
										getSelectedDateFilterOrDefault(0, minDate),
									)}
								</div>
								<div>
									{new Intl.DateTimeFormat(navigator.language).format(
										getSelectedDateFilterOrDefault(1, maxDate),
									)}
								</div>
							</Stack>
						</>
					)}
				</div>
			);
		}
		return <Text>There are no dates saved for this field.</Text>;
	}
}

export default observer(FilterOptionsList);
